/**
 * EventTracker - Batched Event Tracking System
 *
 * Queues analytics events and sends them to the API in batches.
 * Features:
 * - Batching: Queue events, send when batch is full
 * - Auto-flush: Timer-based flush for low-traffic periods
 * - Retry: Exponential backoff on failures
 * - Graceful degradation: Never blocks the main thread
 *
 * Attribution is handled server-side - events are sent with visitorId
 * so the server can query all events for a visitor and calculate credits.
 */

import { Logger } from '../core/Logger';

import type { TrackingEvent } from '../types/sdk.types';
import type { EventTrackerConfig, EventBatch, SendResult, IntelligencePayload } from '../types/tracking.types';
import type { UtmParams } from '../utm';
import { detectAppSurface } from '../app-surface';

/**
 * Default configuration values
 */
const DEFAULT_CONFIG = {
  batchSize: 10,
  flushInterval: 5000, // 5 seconds
  maxRetries: 3,
  debug: false,
  onActivity: () => {},
};

/**
 * EventTracker - Handles batched event tracking
 */
export class EventTracker {
  private logger: Logger;
  private config: Required<EventTrackerConfig>;

  // Event queue
  private queue: TrackingEvent[] = [];

  // Auto-flush timer
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  // Prevent concurrent flushes
  private isFlushing: boolean = false;

  // Track if destroyed
  private isDestroyed: boolean = false;

  // Latest intelligence data to include with event batches
  private latestIntelligence: IntelligencePayload | null = null;

  // UTM attribution params to include with event batches
  private utmParams: UtmParams | null = null;

  constructor(config: EventTrackerConfig) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };

    this.logger = new Logger('[EventTracker]');

    // Initialize UTM params from config (set at SDK init time)
    this.utmParams = config.utmParams ?? null;

    if (this.config.debug) {
      this.logger.info('EventTracker initialized', {
        batchSize: this.config.batchSize,
        flushInterval: this.config.flushInterval,
        visitorId: this.config.visitorId,
      });
    }

    // Start auto-flush timer
    this.startAutoFlush();
  }

  // ==========================================
  // Public API
  // ==========================================

  /**
   * Track an event (adds to queue)
   * Automatically flushes when batch size is reached
   * Events are sent to server with visitorId for server-side attribution
   *
   * NOTE: Hamla does NOT enforce consent - that's the merchant's responsibility.
   * Like Google Analytics, we track everything. Merchants must implement
   * their own cookie banners and privacy policies per their local laws.
   */
  track(event: TrackingEvent): void {
    if (this.isDestroyed) {
      this.logger.warn('EventTracker is destroyed, ignoring event');
      return;
    }

    this.queue.push(event);
    this.config.onActivity();

    if (this.config.debug) {
      this.logger.info('Event tracked', {
        type: event.type,
        campaignId: event.campaignId,
        queueSize: this.queue.length,
      });
    }

    // Flush if batch size reached
    if (this.queue.length >= this.config.batchSize) {
      this.flush().catch((error) => {
        this.logger.error('Auto-flush failed', error);
      });
    }
  }

  /**
   * Flush all queued events to the API
   * Returns result of the send operation
   */
  async flush(): Promise<SendResult> {
    // Skip if already flushing, destroyed, or empty queue
    if (this.isFlushing || this.isDestroyed || this.queue.length === 0) {
      return {
        success: true,
        eventsSent: 0,
      };
    }

    this.isFlushing = true;

    // Take events from queue
    const events = [...this.queue];
    this.queue = [];

    try {
      const result = await this.sendWithRetry(events);

      if (!result.success) {
        // Put events back in queue on failure
        this.queue = [...events, ...this.queue];
      }

      return result;
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Flush queued events using navigator.sendBeacon, which survives the page
   * being hidden or unloaded — the delivery path fetch() cannot guarantee on
   * mobile (where 'beforeunload' never fires and a normal fetch is cancelled).
   * Falls back to a keepalive fetch. Best-effort and synchronous-safe: never
   * awaits and never throws, so it is safe to call from a visibilitychange /
   * pagehide handler.
   *
   * @param finalEvent optional last event to enqueue before sending.
   */
  flushViaBeacon(finalEvent?: TrackingEvent): void {
    if (this.isDestroyed) return;
    if (finalEvent) this.queue.push(finalEvent);
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    let payload: string;
    try {
      payload = JSON.stringify(this.buildBatch(events));
    } catch (error) {
      // Could not serialize — restore the queue and bail rather than lose data.
      this.queue = [...events, ...this.queue];
      this.logger.error('flushViaBeacon: failed to serialize batch', error);
      return;
    }

    const url = `${this.config.apiEndpoint}/api/sdk/events`;

    // Preferred path: sendBeacon (guaranteed delivery on page hide/unload).
    try {
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function'
      ) {
        const blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch {
      // sendBeacon can throw (e.g. payload too large) — fall through to fetch.
    }

    // Fallback: keepalive fetch also survives unload for small payloads.
    try {
      if (typeof fetch === 'function') {
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {
          /* best-effort — nothing more we can do during unload */
        });
      }
    } catch {
      // Never throw from a page-hide handler.
    }
  }

  /**
   * Start auto-flush timer
   */
  startAutoFlush(): void {
    if (this.flushTimer) {
      return; // Already running
    }

    this.flushTimer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush().catch((error) => {
          this.logger.error('Auto-flush failed', error);
        });
      }
    }, this.config.flushInterval);

    if (this.config.debug) {
      this.logger.info('Auto-flush started', {
        interval: this.config.flushInterval,
      });
    }
  }

  /**
   * Stop auto-flush timer
   */
  stopAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;

      if (this.config.debug) {
        this.logger.info('Auto-flush stopped');
      }
    }
  }

  /**
   * Get number of pending events in queue
   */
  getPendingCount(): number {
    return this.queue.length;
  }

  /**
   * Get all pending events (for debugging)
   */
  getPendingEvents(): TrackingEvent[] {
    return [...this.queue];
  }

  /**
   * Update session ID (e.g., when session changes)
   */
  resetIdentity(visitorId: string): void {
    // A failed flush must not relabel queued events as belonging to the next visitor.
    this.queue = [];
    this.config.visitorId = visitorId;
  }

  updateSessionId(sessionId: string): void {
    this.config.sessionId = sessionId;

    if (this.config.debug) {
      this.logger.info('Session ID updated', { sessionId });
    }
  }

  /**
   * Update business ID (e.g., after platformId resolution)
   */
  updateBusinessId(businessId: string): void {
    this.config.businessId = businessId;

    if (this.config.debug) {
      this.logger.info('Business ID updated', { businessId });
    }
  }

  /**
   * Update intelligence data to include with event batches
   * Called by HamlaSDK after orchestration decisions
   */
  updateIntelligence(intelligence: IntelligencePayload): void {
    this.latestIntelligence = intelligence;

    if (this.config.debug) {
      this.logger.info('Intelligence updated', {
        intentScore: intelligence.intentScore,
        journeyStage: intelligence.journeyStage,
      });
    }
  }

  /**
   * Update UTM params to include with event batches
   * Called by HamlaSDK after UTM extraction (if not set at construction time)
   */
  updateUtmParams(utmParams: UtmParams | null): void {
    this.utmParams = utmParams;

    if (this.config.debug) {
      this.logger.info('UTM params updated', { utmParams });
    }
  }

  /**
   * Track a generic custom event by name.
   * Used by HamlaSDK.track() to support arbitrary merchant-defined events.
   * The event name is used verbatim as the type.
   *
   * Stamped `deliberate` because the page's own code chose to send it — the
   * property that lets the server treat it as "a fact the business sent us"
   * (campaign-triggerable), unlike the autocapture events sharing this batch.
   *
   * @example
   * eventTracker.trackCustomEvent('appointment_booked', { date: '2026-04-15', price: 100 });
   */
  trackCustomEvent(eventName: string, properties: Record<string, unknown> = {}): void {
    this.track({
      type: eventName as any,
      timestamp: Date.now(),
      metadata: properties,
      deliberate: true,
    });
  }

  /**
   * Destroy the tracker and cleanup
   * Attempts to flush remaining events before destroying
   */
  async destroy(): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    // Stop auto-flush first to prevent new flushes from starting
    this.stopAutoFlush();

    // Try to flush remaining events BEFORE marking as destroyed
    if (this.queue.length > 0) {
      try {
        await this.flush();
      } catch (error) {
        this.logger.error('Final flush failed on destroy', error);
      }
    }

    // Now mark as destroyed
    this.isDestroyed = true;
    this.queue = [];

    if (this.config.debug) {
      this.logger.info('EventTracker destroyed');
    }
  }

  // ==========================================
  // Private Methods
  // ==========================================

  /**
   * Send events to API with retry logic
   */
  private async sendWithRetry(events: TrackingEvent[]): Promise<SendResult> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
      try {
        if (this.config.debug) {
          this.logger.info(`Send attempt ${attempt}/${this.config.maxRetries}`, {
            eventCount: events.length,
          });
        }

        await this.sendToAPI(events);

        if (this.config.debug) {
          this.logger.info('Events sent successfully', {
            count: events.length,
            attempt,
          });
        }

        return {
          success: true,
          eventsSent: events.length,
        };
      } catch (error) {
        lastError = error as Error;
        this.logger.error(`Send attempt ${attempt} failed`, error);

        // Wait before retry (exponential backoff)
        if (attempt < this.config.maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
          await this.sleep(delay);
        }
      }
    }

    return {
      success: false,
      eventsSent: 0,
      error: lastError?.message || 'Unknown error',
    };
  }

  /**
   * Build the wire batch shared by the fetch and sendBeacon delivery paths, so
   * a beacon-sent batch is byte-for-byte the same shape the server already
   * validates for a normal flush.
   */
  private buildBatch(events: TrackingEvent[]): EventBatch {
    return {
      events,
      businessId: this.config.businessId,
      platformId: this.config.platformId,
      platform: this.config.platform,
      sessionId: this.config.sessionId,
      visitorId: this.config.visitorId, // Required for server-side attribution
      timestamp: Date.now(),
      sessionMetadata: this.getSessionMetadata(),
      // Include intelligence data for Contact enrichment (intent score, journey stage)
      intelligence: this.latestIntelligence || undefined,
      // Include UTM params for event attribution
      utmParams: this.utmParams || undefined,
    };
  }

  /**
   * Send batch to API
   */
  private async sendToAPI(events: TrackingEvent[]): Promise<void> {
    const batch = this.buildBatch(events);

    const url = `${this.config.apiEndpoint}/api/sdk/events`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(batch),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  /**
   * Collect session metadata from the browser context
   */
  private getSessionMetadata(): EventBatch['sessionMetadata'] {
    try {
      return {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        pageType: this.detectPageType(),
        // Sent on every batch, not only at init: the events route records the
        // visit too, and an app shell that never marked its UA is otherwise
        // recorded there as web.
        appSurface: detectAppSurface(),
      };
    } catch {
      // In non-browser environments, return undefined
      return undefined;
    }
  }

  /**
   * Detect page type based on URL patterns
   */
  private detectPageType(): string | undefined {
    if (typeof window === 'undefined') return undefined;

    const path = window.location.pathname.toLowerCase();
    const url = window.location.href.toLowerCase();

    // Common e-commerce page patterns
    if (path.includes('/cart') || path.includes('/basket')) return 'cart';
    if (path.includes('/checkout') || path.includes('/payment')) return 'checkout';
    if (path.includes('/product') || path.includes('/p/') || path.match(/\/products?\//)) return 'product';
    if (path.includes('/category') || path.includes('/collection') || path.includes('/c/')) return 'category';
    if (path.includes('/search') || url.includes('?q=') || url.includes('?search=')) return 'search';
    if (path.includes('/account') || path.includes('/profile') || path.includes('/my-')) return 'account';
    if (path.includes('/order') || path.includes('/confirmation') || path.includes('/thank')) return 'order_confirmation';
    if (path === '/' || path === '' || path.includes('/home')) return 'home';

    return 'other';
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
